/**
 * Resizable - Behavior wrapper that adds resize handles in editor mode
 *
 * Provides 8 resize handles (corners + edges) when block is selected.
 * Supports:
 * - maintainAspectRatio: Lock aspect ratio during resize
 * - autoHeight: Only allow width resize (height auto-expands with content)
 * - playResize: Special play scaling behavior
 * - minWidth/minHeight: Minimum dimensions
 */

import React, { useState, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { useBlockRenderContext } from '@/components/blocks'
import './resizable.css'

interface ResizableProps {
	blockId: string
	children: ReactNode
	maintainAspectRatio?: boolean
	autoHeight?: boolean
	playResize?: boolean
	minWidth?: number
	minHeight?: number
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

interface ResizeState {
	handle: ResizeHandle
	startX: number
	startY: number
	startWidth: number
	startHeight: number
	startPosX: number
	startPosY: number
}

export function Resizable({
	blockId,
	children,
	maintainAspectRatio = false,
	autoHeight = false,
	playResize = false,
	minWidth = 50,
	minHeight = 50,
}: ResizableProps) {
	const { mode, onResize, selectedIds } = useBlockRenderContext()
	const isSelected = selectedIds?.has(String(blockId))
	const [resizing, setResizing] = useState<ResizeState | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	const handleResizeStart = useCallback(
		(handle: ResizeHandle, e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()

			if (!containerRef.current) return

			const rect = containerRef.current.getBoundingClientRect()
			setResizing({
				handle,
				startX: e.clientX,
				startY: e.clientY,
				startWidth: rect.width,
				startHeight: rect.height,
				startPosX: rect.left,
				startPosY: rect.top,
			})
		},
		[]
	)

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!resizing || !onResize) return

			const deltaX = e.clientX - resizing.startX
			const deltaY = e.clientY - resizing.startY

			let newWidth = resizing.startWidth
			let newHeight = resizing.startHeight
			let newX = resizing.startPosX
			let newY = resizing.startPosY

			// Calculate new dimensions based on handle
			const handle = resizing.handle
			if (handle.includes('e')) {
				newWidth = Math.max(minWidth, resizing.startWidth + deltaX)
			}
			if (handle.includes('w')) {
				newWidth = Math.max(minWidth, resizing.startWidth - deltaX)
			}
			if (handle.includes('s') && !autoHeight) {
				newHeight = Math.max(minHeight, resizing.startHeight + deltaY)
			}
			if (handle.includes('n') && !autoHeight) {
				newHeight = Math.max(minHeight, resizing.startHeight - deltaY)
			}

			// Maintain aspect ratio if needed
			if (maintainAspectRatio) {
				const aspectRatio = resizing.startWidth / resizing.startHeight
				if (handle.includes('e') || handle.includes('w')) {
					newHeight = newWidth / aspectRatio
				} else {
					newWidth = newHeight * aspectRatio
				}
			}

			// Call resize callback
			onResize(String(blockId), {
				width: newWidth,
				height: autoHeight ? undefined : newHeight,
			})
		},
		[resizing, onResize, blockId, maintainAspectRatio, autoHeight, minWidth, minHeight]
	)

	const handleMouseUp = useCallback(() => {
		setResizing(null)
	}, [])

	// Set up global mouse event listeners during resize
	useEffect(() => {
		if (resizing) {
			window.addEventListener('mousemove', handleMouseMove)
			window.addEventListener('mouseup', handleMouseUp)
			return () => {
				window.removeEventListener('mousemove', handleMouseMove)
				window.removeEventListener('mouseup', handleMouseUp)
			}
		}
	}, [resizing, handleMouseMove, handleMouseUp])

	// Only show handles in editor mode when selected
	if (mode !== 'editor' || !isSelected) {
		return <>{children}</>
	}

	// Determine which handles to show
	const handles: ResizeHandle[] = autoHeight
		? ['w', 'e'] // Only horizontal resize
		: ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

	return (
		<div ref={containerRef} className="resizable-container">
			{children}
			{/* Resize handles */}
			{handles.map((handle) => (
				<div
					key={handle}
					className={`resize-handle resize-handle--${handle}`}
					onMouseDown={(e) => handleResizeStart(handle, e)}
					role="button"
					aria-label={`Resize ${handle}`}
				/>
			))}
		</div>
	)
}
